// TALKING TO THE UNIVERSAL INPUT ABOUT AN OPEN FILE (Figma "Task creation design", frames
// 70–73 "start chat 1–4" and m70–m73; FIGMA-DUMPS "File briefing box", Pouria 2026-10-15).
//
// A saved file is open as a Briefing card inside the box (frames 60–63, stream S4). A question typed
// under it does NOT leave for Home: the middle is headed "<Ongoing> . conversation", shows the
// empty hero ("What would you like to know?") until the first answer, and then the app's ordinary
// chat — the question right-aligned in blue, the dot, the answer's bullets, copy / regenerate — with
// the file Briefing still in the box underneath (collapsible on desktop; collapsing itself on a phone
// once the answer lands) and the answer's follow-ups in the strip above the box.
//
// Nothing here is new chat machinery: the turns are store/modules/timelineSearch.js's, drawn by
// components/Home/AiAnswer.vue, asked in the scope grammar busy-main-bff#20 already speaks —
// `object:document:<id>` (lib/askScope.js, `ScopeParser` on the BFF). What IS new on the wire is that
// the BFF reads that document's extracted text for the question (busy-main-bff, this batch), so the
// answer is about the file and not only about the thread it arrived on.
//
// This module is the pure half — the words, the scope, the title, the composer's prompts and which
// answer sources are the file itself — so the state machine is testable without a store or a box.

/** Every word the frames put on screen for this journey, in one place. */
export const FILE_CONVERSATION_TEXT = Object.freeze({
  /** the composer before and after an answer (frames 70/72: "Ask about this document or search anything....") */
  ask: 'Ask about this document or search anything....',
  /** the phone's composer once an answer is in (m72) */
  mobileAsk: 'Ask or search anything...',
  /** the phone's running line (m71) — the Global conversation's own word */
  mobileThinking: 'Thinking...',
  /** the hero's two lines (frame 70; the phone, m70, draws the first alone) */
  heroTitle: 'What would you like to know?',
  heroSub: 'Ask about this document or search anything',
  /** the header's suffix — desktop frames spell the separator " . ", the phone frames " · " */
  suffix: 'conversation',
  /** what a file with no Ongoing is headed with */
  untitled: 'File',
})

/** The header's chat glyph (ChatIcon 'chat', 20px) — the same bubble the Global conversation wears. */
export const FILE_CONVERSATION_ICON = 'chat'

const clean = (v) => String(v ?? '').replace(/\s+/g, ' ').trim()

/**
 * The scope a file conversation is asked in — `object:document:<id>`, the object form of the
 * grammar lib/askScope.js writes and busy-main-bff#20 parses. Home when there is no file, so a
 * caller that lost its document still asks something sensible rather than a 400.
 */
export function fileConversationScope(documentId) {
  const id = clean(documentId)
  return id ? `object:document:${id}` : 'home'
}

/**
 * The middle's header: "Buy sunglasses . conversation" on a desktop (frames 70–73),
 * "Buy sunglasses · conversation" on a phone (m70–m73). The Ongoing's title, never the file's name —
 * the file is named by the Briefing card in the box.
 */
export function fileConversationTitle(ongoingTitle, { mobile = false } = {}) {
  const name = clean(ongoingTitle) || FILE_CONVERSATION_TEXT.untitled
  return `${name} ${mobile ? '·' : '.'} ${FILE_CONVERSATION_TEXT.suffix}`
}

// What a file is CALLED in the running line — the document class busy-document-srv decided
// (DocumentResponse.class: pdf, image, invoice, receipt, spreadsheet) read as a word the sentence
// can carry. Frame 71 reads "Checking the receipt and related emails..." for sunglassesreceipt.pdf.
const KIND_WORDS = Object.freeze({
  receipt: 'receipt',
  invoice: 'invoice',
  spreadsheet: 'spreadsheet',
  image: 'image',
  pdf: 'document',
})

/** The noun for a file of this class — "document" for anything the class does not name. */
export function fileKindWord(kind) {
  return KIND_WORDS[clean(kind).toLowerCase()] || 'document'
}

/**
 * The desktop composer's running line (frame 71, cyan shimmer): "Checking the receipt and related
 * emails...". The phone keeps "Thinking..." (m71).
 */
export function fileThinkingPlaceholder(kind, { mobile = false } = {}) {
  if (mobile) return FILE_CONVERSATION_TEXT.mobileThinking
  return `Checking the ${fileKindWord(kind)} and related emails...`
}

/**
 * Which prompt the composer wears while the box is about a file. Null when it is not — the caller
 * then falls through to its own ladder, as it does for the email conversation's prompts.
 */
export function fileConversationPlaceholder({ open = false, answered = false, mobile = false } = {}) {
  if (!open) return null
  // The phone's prompt shortens once an answer is in (m72); before it the frame types the question
  // over the same words the desktop uses (m70 shows the typed question — the prompt under it is the
  // desktop's).
  if (mobile && answered) return FILE_CONVERSATION_TEXT.mobileAsk
  return FILE_CONVERSATION_TEXT.ask
}

/**
 * Is this answer source the file the conversation is ABOUT? (frames 72/73: no "1 ATTACHMENT ·
 * Sunglasses receipt.pdf" chip under the answer — the file is already in the box.)
 * By id first; by file name when the source carries no id, as a restored turn's can.
 */
export function isOwnFileSource(source, { documentId = '', fileName = '' } = {}) {
  if (!source || typeof source !== 'object') return false
  if (clean(source.type).toLowerCase() !== 'document') return false
  const id = clean(documentId)
  const sid = clean(source.documentId || source.id)
  if (id && sid) return id === sid
  const name = clean(fileName).toLowerCase()
  const sname = clean(source.filename || source.fileName || source.name).toLowerCase()
  return !!name && name === sname
}

/** The answer's sources with the conversation's own file taken out. */
export function withoutOwnFile(sources, file) {
  const list = Array.isArray(sources) ? sources : []
  if (!file || (!clean(file.documentId) && !clean(file.fileName))) return list
  return list.filter(s => !isOwnFileSource(s, file))
}

/**
 * Has the conversation about this file been ANSWERED? (frame 72: the card drops its "Possible
 * follow-ups"; m72: the card collapses) — one turn that is no longer being asked and has an answer
 * or blocks.
 */
export function fileConversationAnswered(turns) {
  return (Array.isArray(turns) ? turns : []).some(t => t && !t.asking
    && (clean(t.answer) || (Array.isArray(t.blocks) && t.blocks.length > 0)))
}

export default fileConversationScope
